import { useEffect, useMemo, useState } from 'react';
import type { Lead, LeadQualif, LeadSource, LeadsListResponse } from '../types';
import { listLeads, updateLead, deleteLead } from '../lib/api';
import { IconRefresh, IconSearch } from '../components/icons';
import { SkeletonRows } from '../components/Skeleton';
import { useToast } from '../lib/toast';
import { relativeDate } from '../lib/format';

interface Props {
  onCountChange?: (n: number) => void;
  onHotCountChange?: (n: number) => void;
}

const QUALIF_OPTIONS: LeadQualif[] = ['Hot', 'Warm', 'Cold', 'Lost'];
const SOURCE_OPTIONS: LeadSource[] = ['newsletter', 'audit', 'workspace', 'organic', 'paid', 'referral', 'unknown'];

const QUALIF_STYLE: Record<LeadQualif, string> = {
  Hot: 'bg-red-50 text-red-700 border-red-200',
  Warm: 'bg-amber-soft text-amber border-amber/30',
  Cold: 'bg-cream-deep text-navy border-line',
  Lost: 'bg-cream-tint text-ink-muted border-line',
};

const SOURCE_STYLE: Record<LeadSource, string> = {
  newsletter: 'bg-cream-deep text-navy',
  audit: 'bg-founders-soft text-founders-deep',
  workspace: 'bg-funds-soft text-funds-deep',
  organic: 'bg-cream-tint text-ink-muted',
  paid: 'bg-amber-soft text-amber',
  referral: 'bg-cream-deep text-navy',
  unknown: 'bg-cream-tint text-ink-faint',
};

/**
 * Leads inbox (PR 6). Liste des leads entrants avec inline edit Qualif, Owner.
 *
 * Pattern : table dense avec dropdown inline pour Qualif (Hot/Warm/Cold/Lost)
 * et input inline pour Owner. Pas de drawer Phase 6, les leads sont des
 * objets simples (email + qualif + owner + notes). Si besoin de gérer notes
 * longues ou tags, on ajoutera un drawer en PR 6.5.
 *
 * Pip sidebar rouge si count Hot > 0 (signal commercial chaud à traiter).
 */
export function LeadsPage({ onCountChange, onHotCountChange }: Props) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [byQualif, setByQualif] = useState<LeadsListResponse['byQualif'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterQualif, setFilterQualif] = useState<'all' | LeadQualif>('all');
  const [filterSource, setFilterSource] = useState<'all' | LeadSource>('all');
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listLeads();
      setLeads(res.leads);
      setByQualif(res.byQualif);
      onCountChange?.(res.count);
      onHotCountChange?.(res.byQualif.Hot || 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (q) {
        const blob = `${l.email ?? ''} ${l.company ?? ''} ${l.notes ?? ''} ${l.owner ?? ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (filterQualif !== 'all' && l.qualif !== filterQualif) return false;
      if (filterSource !== 'all' && l.source !== filterSource) return false;
      return true;
    });
  }, [leads, search, filterQualif, filterSource]);

  async function patchField<K extends keyof Lead>(id: string, key: K, value: Lead[K]) {
    setLeads((list) => list.map((l) => (l.id === id ? { ...l, [key]: value } : l)));
    try {
      await updateLead(id, { [key]: value } as Partial<Lead>);
      if (key === 'qualif') refresh();
    } catch (e) {
      toast.error('Update échoué', e instanceof Error ? e.message : String(e));
      refresh();
    }
  }

  async function onDelete(id: string) {
    if (!confirm('Supprimer ce lead ?')) return;
    try {
      await deleteLead(id);
      setLeads((list) => list.filter((l) => l.id !== id));
      refresh();
      toast.success('Lead supprimé', '');
    } catch (e) {
      toast.error('Suppression échouée', e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="px-4 md:px-8 py-6">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-display tracking-tightish text-ink">Leads</h1>
          <p className="mt-1 text-[13.5px] text-ink-muted max-w-2xl">
            Leads entrants par source (newsletter, audit, workspace, organic, paid). Qualif Hot
            d&apos;abord, owner, tags.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition disabled:opacity-50 flex items-center gap-1.5"
        >
          <IconRefresh className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          <span className="font-medium">Erreur : </span>{error}
        </div>
      )}

      {/* KPIs cliquables */}
      {byQualif && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {QUALIF_OPTIONS.map((q) => (
            <button
              key={q}
              onClick={() => setFilterQualif(filterQualif === q ? 'all' : q)}
              className={
                'rounded-md border p-3 shadow-card text-left transition ' +
                (filterQualif === q ? 'border-navy bg-white' : 'border-line-soft bg-cream hover:border-line')
              }
            >
              <p className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">{q}</p>
              <p className="text-[22px] font-display tracking-tightish text-ink mt-1 num">{byQualif[q]}</p>
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-line-soft bg-cream p-3 shadow-card">
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Recherche</label>
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Email, company, notes..."
              className="h-9 w-full pl-8 pr-3 rounded-md border border-line bg-white text-[13px]"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Source</label>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value as 'all' | LeadSource)}
            className="h-9 rounded-md border border-line bg-white px-2.5 text-[13px]"
          >
            <option value="all">Toutes</option>
            {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="ml-auto text-[12px] text-ink-muted self-end">
          <span className="num">{filtered.length}</span> sur <span className="num">{leads.length}</span>
        </div>
      </div>

      <div className="rounded-md border border-line-soft bg-cream shadow-card overflow-hidden">
        {loading && leads.length === 0 ? (
          <div className="p-3"><SkeletonRows rows={5} cols={5} /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[13.5px] text-ink font-medium">
              {leads.length === 0
                ? 'Aucun lead pour le moment.'
                : 'Aucun lead pour ces filtres.'}
            </p>
            <p className="mt-1 text-[12px] text-ink-muted max-w-md mx-auto">
              {leads.length === 0
                ? "Les leads arrivent depuis les opt-ins du site (newsletter, audit, workspace)."
                : 'Élargis les filtres ou la recherche.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-wider text-ink-faint font-medium bg-cream-tint border-b border-line-soft">
                <tr>
                  <th className="px-3 py-2 text-left">Qualif</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Owner</th>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Last activity</th>
                  <th className="px-3 py-2 text-left w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft">
                {filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-cream-tint">
                    <td className="px-3 py-2">
                      <select
                        value={l.qualif ?? 'Cold'}
                        onChange={(e) => patchField(l.id, 'qualif', e.target.value as LeadQualif)}
                        className={'text-[11px] font-medium border rounded-full px-2 py-0.5 ' + (QUALIF_STYLE[l.qualif ?? 'Cold'])}
                      >
                        {QUALIF_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-ink font-mono text-[12px] truncate max-w-[14rem]">{l.email || '-'}</td>
                    <td className="px-3 py-2">
                      {l.source ? (
                        <span className={'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ' + (SOURCE_STYLE[l.source] || 'bg-cream-tint text-ink-muted')}>
                          {l.source}
                        </span>
                      ) : <span className="text-ink-faint">-</span>}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        value={l.owner ?? ''}
                        onChange={(e) => setLeads((list) => list.map((x) => x.id === l.id ? { ...x, owner: e.target.value } : x))}
                        onBlur={(e) => patchField(l.id, 'owner', e.target.value)}
                        placeholder="non assigné"
                        className="text-[12px] bg-transparent w-full focus:outline-none focus:bg-white focus:border focus:border-line rounded px-1 py-0.5"
                      />
                    </td>
                    <td className="px-3 py-2 text-ink-muted text-[12px] truncate max-w-[12rem]">{l.company || '-'}</td>
                    <td className="px-3 py-2 text-ink-faint text-[12px]">
                      {l.lastActivity ? relativeDate(l.lastActivity) : (l.firstSeen ? relativeDate(l.firstSeen) : '-')}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => onDelete(l.id)} className="text-[11px] text-red-700 hover:underline">Suppr.</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
